import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { logOut, logIn } from 'redux/auth/slice';
import {
  useGetCustomersListQuery,
  useChooseCustomerMutation,
  authApi,
} from 'redux/Api/authApi/authApi';
import { ordersApi } from 'redux/Api/ordersApi/ordersApi';
import { beneficiariesApi } from 'redux/Api/beneficiariesApi/beneficiariesApi';
import { employedsApi } from 'redux/Api/employedsApi/employedsApi';
import { operationsApi } from 'redux/Api/operationsApi/operationsApi';
import { profileApi } from 'redux/Api/profileApi/ProfileApi';
import s from './LoginCompanySelect.module.scss';
import logo from 'pages/Login/img/logo.svg';
import { getCompanyInitials } from 'utils/strActions';
import { Loader } from '@mantine/core';

interface Invitation {
  id: number;
  customer: {
    id: number;
    jur_name: string;
  };
}

export const LoginCompanySelect: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isAutoLogging, setIsAutoLogging] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const dispatch = useDispatch();
  const isLoggingInRef = useRef(false);
  const autoLoginTimerRef = useRef<number | null>(null);
  const [isLoadingApproved, setIsLoadindApproved] = useState(false);
  const [isLoadingCancel, setIsLoadindCancel] = useState(false);

  const {
    data: companies,
    isLoading: companiesLoading,
    error: companiesError,
  } = useGetCustomersListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  const [chooseCustomer] = useChooseCustomerMutation();

  const removeInvitation = useCallback((invitationId: number) => {
    setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
  }, []);

  const handleLogin = useCallback(
    async (companyId: number) => {
      if (!companyId) return;

      if (isLoggingInRef.current) return;

      if (autoLoginTimerRef.current) {
        clearTimeout(autoLoginTimerRef.current);
        autoLoginTimerRef.current = null;
      }

      isLoggingInRef.current = true;
      setLoading(true);

      try {
        const response = await chooseCustomer({ customer_id: companyId }).unwrap();
        if (response.token) {
          localStorage.setItem('token', response.token);

          const roleToStore = response.role === 'employee' ? 'director' : response.role;
          localStorage.setItem('role', roleToStore);
          localStorage.setItem('can_manage', response.can_manage ? 'true' : 'false');
          localStorage.removeItem('showCompanySelect');

          dispatch(logIn(response.token));

          dispatch(ordersApi.util.resetApiState());
          dispatch(beneficiariesApi.util.resetApiState());
          dispatch(employedsApi.util.resetApiState());
          dispatch(operationsApi.util.resetApiState());
          dispatch(profileApi.util.resetApiState());

          await new Promise((resolve) => setTimeout(resolve, 300));

          const baseUrl = process.env.REACT_APP_BASE_URL || '';
          const targetPath = roleToStore === 'director' ? 'orders' : 'beneficiaries';
          const targetPage =
            baseUrl === '/' ? `/${targetPath}` : `${baseUrl}/${targetPath}`;

          window.location.replace(targetPage);
        } else {
          console.log('Ошибка авторизации - токен не получен');
        }
      } catch (error: unknown) {
        console.error('Произошла ошибка при выборе компании', error);
      } finally {
        setLoading(false);
        isLoggingInRef.current = false;
      }
    },
    [chooseCustomer, dispatch],
  );

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/invitations`,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        );
        const data = await response.json();
        setInvitations(data);
      } catch (error) {
        console.error('Ошибка при загрузке приглашений:', error);
      }
    };
    fetchInvitations();
  }, []);

  useEffect(() => {
    if (companies && companies.length === 1 && !loading && !isLoggingInRef.current) {
      setIsAutoLogging(true);
      autoLoginTimerRef.current = window.setTimeout(() => {
        handleLogin(companies[0].id);
      }, 500);

      return () => {
        if (autoLoginTimerRef.current) {
          clearTimeout(autoLoginTimerRef.current);
          autoLoginTimerRef.current = null;
        }
      };
    }
  }, [companies, loading, handleLogin]);

  const handleAcceptOrRequestInvitation = async (id: number, action: string) => {
    action === 'accept' ? setIsLoadindApproved(true) : setIsLoadindCancel(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/invitations/${id}?action=${action}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      );
      return response.json();
    } catch (error) {
      console.error('Ошибка при обработке приглашения:', error);
      throw error;
    } finally {
      setIsLoadindApproved(false);
      setIsLoadindCancel(false);
    }
  };

  const handleLogout = () => {
    dispatch(logOut());
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('phone');
    localStorage.removeItem('can_manage');
    localStorage.removeItem('showCompanySelect');

    dispatch(authApi.util.resetApiState());
    dispatch(ordersApi.util.resetApiState());
    dispatch(beneficiariesApi.util.resetApiState());
    dispatch(employedsApi.util.resetApiState());
    dispatch(operationsApi.util.resetApiState());
    dispatch(profileApi.util.resetApiState());

    window.location.reload();
  };

  if (isAutoLogging) {
    return null;
  }

  if (companiesLoading && !isAutoLogging) {
    return (
      <div className={s.container}>
        <div className={s.inner}>
          <div className={s.logo}>
            <img src={logo} alt='Скилла' />
          </div>
          <div className={s.loading}>Загрузка компаний...</div>
        </div>
      </div>
    );
  }

  if (companiesError || !companies) {
    const isAuthError =
      companiesError && 'status' in companiesError && companiesError.status === 401;

    return (
      <div className={s.container}>
        <div className={s.inner}>
          <div className={s.logo}>
            <img src={logo} alt='Скилла' />
          </div>
          <div className={s.error}>
            {isAuthError
              ? 'Ошибка авторизации. Пожалуйста, войдите заново.'
              : 'Ошибка загрузки списка компаний'}
          </div>
          <button className={s.loginButton} onClick={handleLogout}>
            Вернуться к входу
          </button>
        </div>
      </div>
    );
  }

  if (companies.length === 0 && invitations.length > 0) {
    const firstInvitation = invitations[0] as any;

    return (
      <div className={s.container}>
        <div className={s.inner} style={{ textAlign: 'left' }}>
          <div className={s.logo}>
            <img src={logo} alt='Скилла' />
          </div>
          <h1 className={s.invitationHeading}>Приглашение</h1>
          <div>
            <p className={s.invitationText}>
              Вам пришло приглашение к сотрудничеству от компании{' '}
              {firstInvitation.customer.jur_name}
            </p>
            <button
              className={s.loginButton}
              onClick={async () => {
                try {
                  await handleAcceptOrRequestInvitation(firstInvitation.id, 'accept');
                  removeInvitation(firstInvitation.id);
                  handleLogin(firstInvitation.customer.id);
                } catch (error) {
                  console.error('Ошибка принятия приглашения:', error);
                }
              }}>
              <div
                style={{
                  display: 'flex',
                  alignContent: 'flex-end',
                  gap: '10px',
                }}>
                Принять {isLoadingApproved && <Loader size='xs' color='white' />}{' '}
              </div>
            </button>
            <button
              className={s.rejectInvitationButton}
              onClick={async () => {
                try {
                  await handleAcceptOrRequestInvitation(firstInvitation.id, 'reject');
                } catch (error) {
                  console.error('Ошибка отклонения приглашения:', error);
                }
                removeInvitation(firstInvitation.id);
              }}>
              <div
                style={{
                  display: 'flex',
                  alignContent: 'flex-end',
                  gap: '10px',
                }}>
                Отклонить {isLoadingCancel && <Loader size='xs' color='#002cfb' />}{' '}
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className={s.container}>
        <div className={s.inner}>
          <div className={s.logo}>
            <img src={logo} alt='Скилла' />
          </div>
          <div className={s.loading}>Нет компаний, доступных для входа</div>
          <button type='button' className={s.backButton} onClick={handleLogout}>
            Назад
          </button>
        </div>
      </div>
    );
  }

  if (companies.length === 1) {
    return (
      <div className={s.container}>
        <div className={s.inner}>
          <div className={s.logo}>
            <img src={logo} alt='Скилла' />
          </div>
          <div className={s.loading}>Вход в компанию...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <div className={s.inner}>
        <div className={s.logo}>
          <img src={logo} alt='Скилла' />
        </div>

        <h1 className={s.title}>Выбери компанию, в которую хочешь войти</h1>

        <div className={s.companiesList}>
          {companies.map((company) => (
            <div
              key={company.id}
              className={s.companyItem}
              onClick={() => {
                if (!loading && !isLoggingInRef.current) {
                  handleLogin(company.id);
                }
              }}>
              <div className={s.companyInitials}>{getCompanyInitials(company.name)}</div>
              <span className={s.companyName}>{company.name}</span>
            </div>
          ))}
        </div>

        <button className={s.exitBtn} onClick={handleLogout} disabled={loading}>
          Выйти
        </button>
      </div>
      <div className={s.footer}>
        <span>© {new Date().getFullYear()} go.skilla.ru</span>
      </div>
    </div>
  );
};
